import {
  CollectedServiceDataDto,
  CollectedServiceDetailDto,
  ScheduledServiceDataDto,
  ScheduledServiceDetailDto,
  ServiceOverviewDto, SubServiceDetailDto,
} from '../generated/backend/models';
import { getEndDate, parseDateString } from './datetime.utils';

export interface ScheduledServiceDetail {
  id: number;
  serviceDescription: string;
  serviceId: number;
  startDate: Date;
  endDate: Date;
  duration: number;
  subServices: Array<SubServiceDetailDto>;
}

export interface SubServiceDetail {
  duration: number;
  id: number;
  name: string;
}

export interface ScheduledServiceData {
  scheduledServiceDetail: ScheduledServiceDetail;
  customerId: number;
  lastName: string;
  firstName: string;
  customerSalutation: string;
}

export interface CollectedServiceDetail {
  id: number;
  duration: number;
  endDate: Date;
  serviceDescription: string;
  serviceId: number;
  startDate: Date;
  remark: string;
  isProcessed: boolean;
  scheduleId: number;
}

export interface CollectedServiceData {
  collectedServiceDetail: CollectedServiceDetail;
  customerId: number;
  lastName: string;
  firstName: string;
  customerSalutation: string;
}

export interface CollectedServicesData {
  collectedServiceDetails: CollectedServiceDetail[];
  customerId: number;
  lastName: string;
  firstName: string;
  customerSalutation: string;
}

export interface ServiceOverview {
  collectedServiceData: CollectedServiceData[];
  scheduledServiceData: ScheduledServiceData[];
  serviceDate: Date;
}

export interface GroupedServiceOverview {
  serviceData: Array<CollectedServicesData | ScheduledServiceData>;
  serviceDate: Date;
}

export const isScheduledServiceData = (
  scheduledServiceData: CollectedServicesData | ScheduledServiceData
): scheduledServiceData is ScheduledServiceData => {
  return !!(scheduledServiceData as ScheduledServiceData)
    .scheduledServiceDetail;
};

export const isCollectedServicesData = (
  collectedServiceData: CollectedServicesData | ScheduledServiceData
): collectedServiceData is CollectedServicesData => {
  return !!(collectedServiceData as CollectedServicesData)
    .collectedServiceDetails;
};

export const isCollectedServiceData = (
  collectedServiceData: CollectedServiceData | ScheduledServiceData
): collectedServiceData is CollectedServiceData => {
  return !!(collectedServiceData as CollectedServiceData)
    .collectedServiceDetail;
};

export const getDetailsFromCollectedAndScheduledServiceData = (
  serviceData: CollectedServiceData | ScheduledServiceData
): CollectedServiceDetail | ScheduledServiceDetail => {
  if (isCollectedServiceData(serviceData)) {
    return serviceData.collectedServiceDetail;
  }
  return serviceData.scheduledServiceDetail;
};

export const getDetailsFromCollectedServicesAndScheduledServiceData = (
  serviceData: CollectedServicesData | ScheduledServiceData
): CollectedServiceDetail | ScheduledServiceDetail => {
  if (isCollectedServicesData(serviceData)) {
    // For grouped services, return the first service's details for positioning
    // This will be used as a fallback, but we'll handle individual services separately
    return serviceData.collectedServiceDetails[0];
  }
  return serviceData.scheduledServiceDetail;
};

export const mergeAndGroupCollectedAndScheduledServiceData = (
  servicesOverview: ServiceOverview[]
): GroupedServiceOverview[] => {
  return servicesOverview.map((serviceOverview) => {
    return {
      serviceDate: serviceOverview.serviceDate,
      serviceData: [
        ...serviceOverview.scheduledServiceData,
        ...serviceOverview.collectedServiceData,
      ]
        .sort((previousService, currentService) => {
          const previousServiceDetail =
            getDetailsFromCollectedAndScheduledServiceData(previousService);
          const currentServiceDetail =
            getDetailsFromCollectedAndScheduledServiceData(currentService);
          return (
            previousServiceDetail.startDate.getTime() -
            currentServiceDetail.startDate.getTime()
          );
        })
        .reduce((accumulator, serviceData, index) => {
          if ((serviceData as ScheduledServiceData).scheduledServiceDetail) {
            return [...accumulator, serviceData];
          }
          if ((serviceData as CollectedServiceData).collectedServiceDetail) {
            const collectedServicesData: CollectedServicesData = {
              collectedServiceDetails: [
                (serviceData as CollectedServiceData).collectedServiceDetail,
              ],
              customerId: serviceData.customerId,
              lastName: serviceData.lastName,
              firstName: serviceData.firstName,
              customerSalutation: serviceData.customerSalutation,
            };
            const lastAddedService = accumulator[accumulator.length - 1];
            if (
              index === 0 ||
              (lastAddedService as ScheduledServiceData)
                .scheduledServiceDetail ||
              !(lastAddedService as CollectedServicesData)
                .collectedServiceDetails ||
              lastAddedService.customerId !== serviceData.customerId
            ) {
              return [...accumulator, collectedServicesData];
            }
            // Same customer and previous item is a collected group: group only if they touch or overlap
            const lastGroup = lastAddedService as CollectedServicesData;
            const lastDetail = lastGroup.collectedServiceDetails[lastGroup.collectedServiceDetails.length - 1];
            const currentDetail = (serviceData as CollectedServiceData).collectedServiceDetail;
            const touchesOrOverlaps = lastDetail.endDate.getTime() >= currentDetail.startDate.getTime();

            if (touchesOrOverlaps) {
              lastGroup.collectedServiceDetails = [
                ...lastGroup.collectedServiceDetails,
                currentDetail,
              ];
              return accumulator;
            }
            // Does not touch/overlap: start a new group for the same customer
            return [...accumulator, collectedServicesData];
          }
          return accumulator;
        }, [] as (ScheduledServiceData | CollectedServicesData | any)[]),
      };
    });
};

export const mapScheduledServiceDetailDto = (
  scheduledServiceDetailDto: ScheduledServiceDetailDto | undefined
): ScheduledServiceDetail => {
  return {
    id: scheduledServiceDetailDto?.id as number,
    duration: scheduledServiceDetailDto?.duration as number,
    startDate: parseDateString(scheduledServiceDetailDto?.startDate),
    endDate: getEndDate(
      scheduledServiceDetailDto?.startDate as string,
      scheduledServiceDetailDto?.duration as number
    ),
    serviceDescription: scheduledServiceDetailDto?.serviceDescription as string,
    serviceId: scheduledServiceDetailDto?.serviceId as number,
    subServices: scheduledServiceDetailDto?.subServices || []
  };
};

export const mapScheduledServiceDataDto = (
  scheduledServicesData: ScheduledServiceDataDto[] | undefined | null
): ScheduledServiceData[] => {
  return (
    scheduledServicesData?.map((scheduledServiceData) => {
      return {
        scheduledServiceDetail: mapScheduledServiceDetailDto(
          scheduledServiceData?.scheduledServiceDetails
        ),
        customerId: scheduledServiceData?.customerId as number,
        lastName: scheduledServiceData?.lastName as string,
        firstName: scheduledServiceData?.firstName as string,
        customerSalutation: scheduledServiceData?.customerSalutation as string,
      };
    }) || []
  );
};

export const mapCollectedServiceDetailDto = (
  collecteServiceDetails: CollectedServiceDetailDto | undefined | null
): CollectedServiceDetail => {
  return {
    id: collecteServiceDetails?.id as number,
    duration: collecteServiceDetails?.duration as number,
    endDate: getEndDate(
      collecteServiceDetails?.startDate as string,
      collecteServiceDetails?.duration as number
    ),
    serviceDescription: collecteServiceDetails?.serviceDescription as string,
    serviceId: collecteServiceDetails?.serviceId as number,
    startDate: parseDateString(collecteServiceDetails?.startDate),
    remark: collecteServiceDetails?.remark as string,
    isProcessed: !!collecteServiceDetails?.isProcessed,
    scheduleId: collecteServiceDetails?.scheduleId as number,
  };
};

export const mapCollectedServiceDetailsDto = (
  collecteServiceDetails: CollectedServiceDetailDto[] | undefined | null
): CollectedServiceDetail[] => {
  return collecteServiceDetails?.map(mapCollectedServiceDetailDto) || [];
};

export const mapCollectedServicesDataDto = (
  collectedServiceData: CollectedServiceDataDto[] | undefined | null
): CollectedServiceData[] => {
  return (
    collectedServiceData?.map((serviceData) => {
      return {
        customerId: serviceData.customerId as number,
        firstName: serviceData.firstName as string,
        lastName: serviceData.lastName as string,
        customerSalutation: serviceData.customerSalutation as string,
        collectedServiceDetail: mapCollectedServiceDetailDto(
          serviceData.collectedServiceDetail
        ),
      };
    }) || []
  );
};

export const mapServiceOverviewDto = (
  servicesData: ServiceOverviewDto[]
): ServiceOverview[] => {
  return servicesData.map((serviceData) => {
    return {
      serviceDate: parseDateString(serviceData.serviceDate),
      collectedServiceData: mapCollectedServicesDataDto(
        serviceData.collectedServiceData
      ),
      scheduledServiceData: mapScheduledServiceDataDto(
        serviceData.scheduledServiceData
      ),
    };
  });
};

export const mapAndGroupServiceOverviewDto = (
  serviceData: ServiceOverviewDto[]
): GroupedServiceOverview[] => {
  return mergeAndGroupCollectedAndScheduledServiceData(
    mapServiceOverviewDto(serviceData)
  );
};

export const servicesAreActive = (services: CollectedServiceDetail[]) => {
  return services.some((service) => !service.isProcessed);
};
